import { autorun } from '../common/stores/mobx-config.js';
import backgroundTaskStore from '../common/stores/backgroundTaskStore.js';
import globalOffcanvasManager from '../utils/OffcanvasManager.js';

/**
 * Contrôleur UI pour le journal des opérations.
 * Rattaché au bouton GEDCOM info (#gedcomInfoButton) dans le header.
 * Observe le backgroundTaskStore via MobX autorun et met à jour :
 * - L'animation de l'icône info quand des tâches de fond tournent
 * - Le contenu du panneau offcanvas (pipeline + tâches de fond)
 */

let initialized = false;

export function initializeBackgroundTaskIndicator() {
    if (initialized) return;
    initialized = true;

    const infoButton = document.getElementById('gedcomInfoButton');
    const tasksList = document.getElementById('background-tasks-list');

    if (!infoButton || !tasksList) {
        console.warn('BackgroundTaskIndicator: éléments DOM manquants');
        return;
    }

    // Click on info button → open journal offcanvas
    infoButton.addEventListener('click', () => {
        globalOffcanvasManager.show('backgroundTasksPanel');
    });

    // Clear journal button
    const clearBtn = document.getElementById('journal-clear-btn');
    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            backgroundTaskStore.clearJournal();
        });
    }

    // MobX autorun — réagit à tout changement observable dans le store
    autorun(() => {
        const activeCount = backgroundTaskStore.activeCount;
        const tasks = backgroundTaskStore.allTasks;

        // Animate info button when background tasks are running
        if (activeCount > 0) {
            infoButton.classList.add('journal-active');
        } else {
            infoButton.classList.remove('journal-active');
        }

        // Render journal in offcanvas
        renderJournal(tasksList, tasks);
    });
}

// ── Rendering ─────────────────────────────────────────────

/**
 * Génère le HTML du journal : pipeline entries + tâches de fond
 */
function renderJournal(container, tasks) {
    if (tasks.length === 0) {
        container.innerHTML = '<p class="text-muted text-center mt-3">Aucune opération enregistrée</p>';
        return;
    }

    const pipelineEntries = tasks.filter(t => t.kind === 'pipeline');
    const taskEntries = tasks.filter(t => t.kind === 'task');

    // Pipeline: chronologique (ancien → récent)
    pipelineEntries.sort((a, b) => a.startTime - b.startTime);

    // Tasks: running first, then chronological
    taskEntries.sort((a, b) => {
        if (a.status === 'running' && b.status !== 'running') return -1;
        if (b.status === 'running' && a.status !== 'running') return 1;
        return a.startTime - b.startTime;
    });

    let html = pipelineEntries.map(renderPipelineEntry).join('');

    if (pipelineEntries.length > 0 && taskEntries.length > 0) {
        html += '<div class="journal-separator"></div>';
    }

    html += taskEntries.map(renderTaskEntry).join('');

    container.innerHTML = html;
}

/**
 * Entrée pipeline compacte (étape GEDCOM)
 */
function renderPipelineEntry(entry) {
    const icon = entry.status === 'failed' ? getStatusIcon('failed') : getStatusIcon('completed');
    const statusClass = entry.status === 'failed' ? 'journal-entry--failed' : 'journal-entry--completed';
    const time = new Date(entry.startTime).toLocaleTimeString('fr-FR', {
        hour: '2-digit', minute: '2-digit', second: '2-digit'
    });

    return `
        <div class="journal-entry ${statusClass}">
            <span class="journal-icon">${icon}</span>
            <span class="journal-label">${entry.label}</span>
            <span class="journal-time">${time}</span>
            ${entry.message ? `<div class="journal-message">${entry.message}</div>` : ''}
        </div>
    `;
}

/**
 * Entrée tâche de fond (géocodage, AI notes — avec progress bar)
 */
function renderTaskEntry(task) {
    const statusClass = `task-item--${task.status}`;
    const icon = getStatusIcon(task.status);
    const elapsed = formatElapsed(task);
    const progressBar = renderProgressBar(task);

    return `
        <div class="task-item ${statusClass}">
            <div class="task-item-header">
                <span class="task-status-icon">${icon}</span>
                <span class="task-label">${task.label}</span>
                <span class="task-time">${elapsed}</span>
            </div>
            ${progressBar}
            ${task.message ? `<div class="task-message">${task.message}</div>` : ''}
        </div>
    `;
}

// ── Helpers ───────────────────────────────────────────────

function getStatusIcon(status) {
    switch (status) {
        case 'running':
            return '<svg class="task-status-spinner" width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="28 10"/></svg>';
        case 'completed':
            return '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>';
        case 'failed':
            return '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>';
        default:
            return '';
    }
}

function renderProgressBar(task) {
    if (task.status !== 'running') return '';
    if (task.progress === null) {
        return `
            <div class="progress task-progress">
                <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 100%"></div>
            </div>`;
    }
    return `
        <div class="progress task-progress">
            <div class="progress-bar" role="progressbar" style="width: ${task.progress}%"
                 aria-valuenow="${task.progress}" aria-valuemin="0" aria-valuemax="100">
                ${task.progress}%
            </div>
        </div>`;
}

function formatElapsed(task) {
    const end = task.endTime || Date.now();
    const seconds = Math.floor((end - task.startTime) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    return `${minutes}m${remaining.toString().padStart(2, '0')}s`;
}
